import React, { useState, useCallback, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import axios from "../../plugins/axios";
import { format } from "date-fns";
import {
    Box,
    Typography,
    Table,
    TableCell,
    TableBody,
    TableHead,
    TableRow,
    Paper,
    Button,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import {
    PostAdd,
} from "@mui/icons-material";
import TimesheetEntryDialog from "./TimesheetEntryDialog";
import { makeStyles } from 'tss-react/mui';

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

const useStyles = makeStyles()(theme => ({
    spinnerContainer: {
        display: 'flex',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center'
    },
    clamped: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        display: "-webkit-box",
        "-webkit-line-clamp": 3,
        "-webkit-box-orient": "vertical",
        fontSize: '0.9em',
        whiteSpace: 'pre-wrap'
    }
}))

const DailyTimeSheet = ({ user, calcDuration, activityTypes, advisoryTypes, filesTooltip }) => {

    const { classes } = useStyles();

    const [dialogEntry, setDialogEntry] = useState(/** @type {Record<string, any> | null} */ (null));
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [timeEntries, setTimeEntries] = useState([]);   

    const onSubmitted = () => {
        getTimeEntries();
        setDialogEntry(null);
    }

    const addEntry = () => setDialogEntry({});
    
    const getTimeEntries = useCallback(async () => {
        try {
            setLoading(true);
            var response = await axios.get(
                `${apiUrl}/employeetimedata/${format(selectedDate, "yyyy-MM-dd")}`
            );
            var entries = response.data || [];
            entries.forEach(
                (e) => (e.duration = calcDuration(e.startTime, e.endTime))
            );

            setTimeEntries(entries);
        } finally {
            setLoading(false);
        }
    }, [calcDuration, selectedDate]);

    useEffect(() => {
        getTimeEntries();

    }, [getTimeEntries]);

    return (
        <Box display="flex" flexDirection="column" style={{ paddingTop: "10px" }} gap="10px">
            <Box display="flex" alignItems="center">
                <Typography variant="h6" style={{ marginRight: "20px" }}>
                    Daily Time Sheet - {user.details.firstName} {user.details.lastName}
                </Typography>
                <DatePicker
                    value={selectedDate}
                    onChange={setSelectedDate}
                    slotProps={{
                        textField: {
                            size: 'small',
                            InputProps: {
                                style: {
                                    width: '150px'
                                }
                            }
                        },
                        field: {
                            readOnly: true,
                        },                        
                    }}
                />
            </Box>
            {loading &&
                <div className={classes.spinnerContainer}>
                    <CircularProgress />
                </div>
            }
            {!loading &&
                <>
                    <Box display="flex" alignItems="center">
                        <Typography variant="h6">Entries</Typography>
                        <Box flexGrow={1}></Box>
                        <Button variant="contained" color="primary" onClick={addEntry}>Add Entry</Button>
                    </Box>
                    {timeEntries.length === 0 &&
                        <Paper>
                            <Box display="flex" flexDirection="column" alignItems="center" gap="5px" p={4}>
                                <Box style={{ borderRadius: '50%', background: '#e5deff', padding: '10px', lineHeight: '1em' }} >
                                    <PostAdd style={{ width: '30px', height: '30px' }} />
                                </Box>
                                <Typography variant="h6">No Entries for today ...</Typography>
                                <Typography>There are currently no entries on this date.</Typography>
                                <Button variant="contained" color="primary" onClick={addEntry}>Add Entry</Button>
                            </Box>
                        </Paper>
                    }
                    {timeEntries.length > 0 &&
                        <Paper>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Id</TableCell>
                                        <TableCell>Account</TableCell>
                                        <TableCell>Contract</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell align="center">Activity Type</TableCell>
                                        <TableCell align="center">Start</TableCell>
                                        <TableCell align="center">End</TableCell>
                                        <TableCell align="center">Duration</TableCell>
                                        <TableCell align="center">Files</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {timeEntries.map((entry) => (
                                        <TableRow key={entry.id}>
                                            <TableCell><a href={entry.url} target="_blank" rel="noreferrer">{entry.name}</a></TableCell>
                                            <TableCell>{entry.accountName}</TableCell>
                                            <TableCell>{entry.contractId && entry.contracts?.find((x) => x.key === entry.contractId)?.value}</TableCell>
                                            <TableCell><Tooltip title={<div style={{whiteSpace: 'pre-wrap'}}>{entry.description}</div>}><div className={classes.clamped}>{entry.description}</div></Tooltip></TableCell>
                                            <TableCell align="center">{entry.activityType}</TableCell>
                                            <TableCell align="center">{format(new Date(entry.startTime), "HH:mm")}</TableCell>
                                            <TableCell align="center">{format(new Date(entry.endTime), "HH:mm")}</TableCell>
                                            <TableCell align="center">{entry.duration}</TableCell>
                                            <TableCell align="center">{filesTooltip(entry.filenames)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    }
                </>
            }
            {dialogEntry &&
                <TimesheetEntryDialog
                    entry={dialogEntry}
                    setEntry={setDialogEntry}
                    date={selectedDate}
                    activityTypes={activityTypes}
                    advisoryTypes={advisoryTypes}
                    calcDuration={calcDuration}
                    onSubmitted={onSubmitted}
                />
            }
        </Box>
    );
};

export default DailyTimeSheet;
